"use client"

import { useSyncExternalStore, useCallback, useRef, useEffect } from "react"
import { getOrCreateCartManager } from "./cartManagerRegistry"
import type { Cart } from "./types"

/**
 * React hook for subscribing to specific parts of cart state.
 * Only re-renders when the selected value changes, preventing unnecessary
 * re-renders for components that only need a subset of cart data.
 *
 * @param region - The region identifier (e.g., 'us', 'eu', 'uk', 'ca')
 * @param channel - The channel identifier
 * @param selector - Function to select specific data from cart state
 * @param equalityFn - Optional function to compare previous and next values (defaults to Object.is)
 * @returns The selected value from cart state
 *
 * @example
 * ```tsx
 * // Only re-renders when item count changes
 * function CartBadge({ region, channel }) {
 *   const itemCount = useCartSelector(region, channel, selectItemCount)
 *   return <Badge>{itemCount}</Badge>
 * }
 *
 * // Only re-renders when total or currency changes
 * function CartTotal({ region, channel }) {
 *   const { total, currency } = useCartSelector(
 *     region,
 *     channel,
 *     (state) => ({ total: state.totals.grandTotal, currency: state.currency }),
 *     (a, b) => a.total === b.total && a.currency === b.currency
 *   )
 *   return <Price value={total} currency={currency} />
 * }
 * ```
 */
export function useCartSelector<T>(
  region: string,
  channel: string,
  selector: (state: Cart.StateData) => T,
  equalityFn: (a: T, b: T) => boolean = Object.is
): T {
  const cartManager = getOrCreateCartManager(region.toLowerCase(), channel)
  const selectorRef = useRef(selector)
  const equalityFnRef = useRef(equalityFn)

  // Update refs in useEffect to comply with React Compiler rules
  useEffect(() => {
    selectorRef.current = selector
  }, [selector])

  useEffect(() => {
    equalityFnRef.current = equalityFn
  }, [equalityFn])

  // Track the previous selected value for comparison
  const UNSET = Symbol("unset")
  const previousValueRef = useRef<T | typeof UNSET>(UNSET)

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      return cartManager.subscribe(() => {
        const newValue = selectorRef.current(cartManager.getState())
        const prevValue = previousValueRef.current

        // Only notify if the selected value actually changed
        if (
          prevValue === UNSET ||
          !equalityFnRef.current(prevValue as T, newValue)
        ) {
          previousValueRef.current = newValue
          onStoreChange()
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartManager]
  )

  const getSnapshot = useCallback(() => {
    return selectorRef.current(cartManager.getState())
  }, [cartManager])

  const getServerSnapshot = useCallback(() => {
    return selectorRef.current(cartManager.getState())
  }, [cartManager])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

// Pre-built selectors for common use cases

/** Selects the number of items in the cart */
export const selectItemCount = (state: Cart.StateData): number =>
  state.items.length

/** Selects the grand total from cart totals */
export const selectGrandTotal = (state: Cart.StateData): number =>
  state.totals.grandTotal

/** Selects the currency code */
export const selectCurrency = (state: Cart.StateData): string => state.currency

/** Selects the cart ID */
export const selectId = (state: Cart.StateData): string => state.id

/** Selects whether the cart is empty */
export const selectIsEmpty = (state: Cart.StateData): boolean =>
  state.items.length === 0

/** Selects the cart items array */
export const selectItems = (state: Cart.StateData): Cart.Item[] => state.items

/** Selects the cart totals object */
export const selectTotals = (state: Cart.StateData): Cart.Totals => state.totals

/** Selects the discount if applied */
export const selectDiscount = (
  state: Cart.StateData
): Cart.Discount | undefined => state.discount

/** Selects the gift card if applied */
export const selectGiftCard = (
  state: Cart.StateData
): Cart.GiftCard | undefined => state.giftCard

/** Selects the current shipping method */
export const selectShippingMethod = (state: Cart.StateData) =>
  state.shippingMethod

/** Selects tax information */
export const selectTax = (state: Cart.StateData): Cart.TaxInfo => state.tax

// Equality functions for complex selectors

/** Shallow equality check for arrays (compares by reference and length) */
export const shallowArrayEqual = <T>(a: T[], b: T[]): boolean =>
  a === b || (a.length === b.length && a.every((item, i) => item === b[i]))

/** Shallow equality check for objects */
export const shallowObjectEqual = <T extends Record<string, unknown>>(
  a: T,
  b: T
): boolean => {
  if (a === b) return true
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every((key) => a[key] === b[key])
}
